import React, { FC, useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { navigate } from "gatsby";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Typography from "../typography/Typography";
import Link from "../link/Link";
import { useBackgroundColor } from "../../utils";
import { HelpTopic, HelpTopicCategory, LinkType } from "../../types";
import LinkList from "../linkList/LinkList";
import Stack from "../stack/Stack";
import { Minus, Plus, Tick } from "../icon";
import Select from "../form/select/Select";

// Interface

export interface HelpTopicArticlesProps {
  helpTopics: HelpTopic[];
  helpTopicCategories: HelpTopicCategory[];
  currentUrl: string;
}

interface StyledTopicLinkProps {
  isActive: boolean;
}

// Styles

const StyledHelpTopicArticles = styled.div`
  ${({ theme: { space, colors, borderRadii, media, maxMedia } }) => css`
    @media (max-width: ${maxMedia.large}) {
      border-radius: ${borderRadii.small};
      overflow: hidden;
      border: solid 1px ${colors.darkBlue};
    }

    @media (min-width: ${media.large}) {
      display: flex;
      grid-template-columns: repeat(2, 1fr);
    }
  `};
`;

const StyledHelpTopics = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    ${useBackgroundColor("neutral")}
    padding-top: ${space.fluidMediumToXxLarge};
    padding-bottom: ${space.fluidMediumToXxLarge};
    padding-left: ${space.fluidSmallToLarge};
    padding-right: ${space.fluidSmallToLarge};
    border-bottom: 1px solid ${colors.darkBlue};

    @media (min-width: ${media.large}) {
      width: 409px;
      border-left: 1px solid ${colors.darkBlue};
      border-top: 1px solid ${colors.darkBlue};
      border-top-left-radius: ${borderRadii.small};
      border-bottom-left-radius: ${borderRadii.small};
    }
  `};
`;

const StyledHelpCategories = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    ${useBackgroundColor("white")}
    padding-top: ${space.fluidMediumToXxLarge};
    padding-bottom: ${space.fluidMediumToXxLarge};
    padding-left: ${space.fluidSmallToXxLarge};
    padding-right: ${space.fluidSmallToXxLarge};

    @media (min-width: ${media.large}) {
      flex-grow: 1;
      border: 1px solid ${colors.darkBlue};
      border-top-right-radius: ${borderRadii.small};
      position: relative;

      &:after {
        content: "";
        background-color: #fff;
        position: absolute;
        top: 100%;
        left: -1px;
        width: calc(100% + 2px);
        height: 48px;
        border-left: 1px solid ${colors.darkBlue};
        border-right: 1px solid ${colors.darkBlue};
        border-bottom: 1px solid ${colors.darkBlue};
        border-bottom-right-radius: ${borderRadii.small};
        border-bottom-left-radius: ${borderRadii.small};
      }
    }
  `};
`;

const StyledHelpTopic = styled.li`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    & + & {
      margin-top: ${space.small};
      padding-top: ${space.small};
      border-top: 1px solid ${colors.darkBlue};
    }
  `};
`;

const StyledHelpTopicCategory = styled.li`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    & + & {
      margin-top: ${space.fluidMediumToLarge};
      padding-top: ${space.fluidMediumToLarge};
      border-top: 1px solid ${colors.darkBlue};
    }
  `};
`;

const StyledDesktopNavigation = styled(Stack)`
  ${({ theme: { space, colors, media, borderRadii, maxMedia } }) => css`
    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

const StyledMobileNavigation = styled.div`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.large}) {
      display: none;
    }
  `};
`;

const StyledTopicLink = styled(Link)<StyledTopicLinkProps>`
  ${({
    theme: { space, colors, media, borderRadii, maxMedia },
    isActive,
  }) => css`
    display: flex;
    justify-content: space-between;

    ${isActive &&
    css`
      ${useBackgroundColor("lilac")}
      border-radius: ${borderRadii.small};
      border: solid 1px currentColor;
      padding: ${space.xSmall};
    `}
  `};
`;

const StyledLinkList = styled(LinkList)<StyledTopicLinkProps>`
  ${({
    theme: { space, colors, media, borderRadii, maxMedia },
    isActive,
  }) => css`
    li:nth-child(n + 4) {
      display: none;
    }

    ${isActive &&
    css`
      li:nth-child(n + 4) {
        display: block;
      }
    `}
  `};
`;

// JSX

const HelpTopicItem: FC<HelpTopic> = ({ name, url, currentUrl }) => {
  const isActive = url === currentUrl;
  return (
    <StyledHelpTopic>
      <StyledTopicLink
        url={url}
        label={name}
        hideArrow={!isActive}
        isActive={isActive}
        dataLayerClickContext="Help center topic article"
      />
    </StyledHelpTopic>
  );
};

const HelpTopicCategoryBlock: FC<HelpTopicCategory> = ({ name, articles }) => {
  const [showMore, setShowMore] = useState(false);

  const handleShowMore = () => {
    setShowMore(!showMore);
  };

  return (
    <StyledHelpTopicCategory>
      <Stack gap="fluidSmallToMedium">
        <Typography component="h2" variant="headingXSmall">
          {name}
        </Typography>
        <StyledLinkList
          items={articles.map((item) => ({
            label: item.name,
            url: item.url,
          }))}
          isActive={showMore}
          variant="secondary"
        />
        {articles.length > 3 && (
          <Link
            label={showMore ? "Show less" : "Show more"}
            onClick={handleShowMore}
            iconRight={
              showMore ? <Minus size="small" /> : <Plus size="small" />
            }
            dataLayerClickContext="Help center topic show more"
          />
        )}
      </Stack>
    </StyledHelpTopicCategory>
  );
};

export const HelpTopicArticles: FC<HelpTopicArticlesProps> = ({
  helpTopics,
  helpTopicCategories,
  currentUrl,
}) => {
  const helpTopicSelectOptions = helpTopics.map((helpTopic) => ({
    label: helpTopic.name,
    value: helpTopic.url,
  }));

  const handleSelectChange = (e: any) => {
    if (e.target.value !== currentUrl) {
      navigate(e.target.value);
    }
  };

  return (
    <StyledHelpTopicArticles>
      <div>
        <StyledHelpTopics>
          <StyledDesktopNavigation gap="large">
            <Typography component="h2" variant="secondaryHeadingSmallBold">
              Help topics
            </Typography>
            <ul>
              {helpTopics.map((helpTopic, index) => (
                <HelpTopicItem
                  key={index}
                  {...helpTopic}
                  currentUrl={currentUrl}
                />
              ))}
            </ul>
          </StyledDesktopNavigation>
          <StyledMobileNavigation>
            <Select
              label="Help topics"
              options={helpTopicSelectOptions}
              defaultValue={currentUrl}
              id="helpTopics"
              onChange={handleSelectChange}
            />
          </StyledMobileNavigation>
        </StyledHelpTopics>
      </div>
      <StyledHelpCategories>
        <ul>
          {helpTopicCategories.map((helpTopicCategory, index) => (
            <HelpTopicCategoryBlock key={index} {...helpTopicCategory} />
          ))}
        </ul>
      </StyledHelpCategories>
    </StyledHelpTopicArticles>
  );
};

export default HelpTopicArticles;
